import { CircularProgress, IconButton, Menu, MenuItem, Popover, Tooltip } from '@material-ui/core';
import { Block, Forward as ContentForward, Info as ActionInfo } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { Map, OrderedMap } from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Permissions } from 'shared/models/permission';
import { hasRole, UserData } from 'shared/models/user';

import { EmailAddress } from 'shared/types/email-compose';
import {
    forwardFlaggedEmail,
    getFlaggedEmails,
    requestCommunicationMatch,
    requestToasterOpen,
    showModalAlert,
    updateCommunicationMatchData
} from '../actions';
import { requestAddSenderToIgnoredList } from '../api';
import { clientCommsEmailAddress } from '../common/email/emails';
import { JobTitle } from '../containers/job-title';
import { PersonName } from '../containers/person-name';
import { Spinner } from '../core-ui/spinner';
import { replyToEmail } from '../email-compose/actions';
import { ComposeEmailWindowData } from '../email-compose/types';
import { Communication, FlaggedEmails as FlaggedEmailsType, RequestErrors, State } from '../state';
import { AddSenderToIgnoredListDialog } from './add-sender-to-ignored-list-dialog';
import { Communication as CommunicationComponent } from './communication';
import { CommunicationReplyButton } from './communication-reply-button';

interface OwnProps {
    selected: string;
    activeUsers: UserData[];
}

interface ConnectedProps {
    emailContentBlacklistedDomains: string;
    flaggedEmails: OrderedMap<string, FlaggedEmailsType>;
    pendingRequests: Map<string, RequestErrors>;
    sessionUser: UserData;
    userPermissions: Permissions;
}

interface ConnectedDispatch {
    getFlaggedEmails: (account: string, offset: number, limit: number) => void;
    updateCommunicationMatchData: (
        communication: Communication,
        data: {
            ignored?: boolean;
            personIds?: string[];
            jobIds?: string[];
            error?: boolean;
            manualOverridePersonIds?: boolean;
            manualOverrideJobIds?: boolean;
            bug?: boolean;
        }
    ) => void;
    requestCommunicationMatch: (communication: Communication) => void;
    replyToEmail: (
        accountOptions: EmailAddress[],
        emailContentBlacklistedDomains: string,
        message: Communication,
        replyAll: boolean,
        archiveOnReply?: boolean,
        composeWindowData?: Partial<ComposeEmailWindowData>
    ) => void;
    showModalAlert: (description: string | JSX.Element, title: string, nonCancelable?: boolean) => void;
    forwardFlaggedEmail: (account: string, messageId: string, email: string) => void;
    requestToasterOpen: (message: string, autoHideDuration?: number) => void;
}

type FlaggedEmailsProps = OwnProps & ConnectedDispatch & ConnectedProps & RouteComponentProps;

interface FlaggedEmailsState {
    currentPage: number;
    moreInfoOpen: string;
    moreInfoRef: HTMLElement | null;

    forwardSelectorOpen: string;
    forwardRef: HTMLElement | null;

    ignoreDialogOpen: string | null;
    ignoreDialogData: Communication | null;
    ignoreInProgress: boolean;
}

const numThreadsPerPage = 20;

class FlaggedEmailsComponent extends React.Component<FlaggedEmailsProps, FlaggedEmailsState> {
    constructor(props: FlaggedEmailsProps) {
        super(props);
        this.state = {
            currentPage: 0,
            forwardRef: null,
            forwardSelectorOpen: null,
            ignoreDialogData: null,
            ignoreDialogOpen: null,
            ignoreInProgress: false,
            moreInfoOpen: null,
            moreInfoRef: null
        };
    }

    componentDidMount() {
        this.props.getFlaggedEmails(this.props.selected, 0, numThreadsPerPage);
    }

    componentDidUpdate(prevProps: FlaggedEmailsProps) {
        if (prevProps.selected !== this.props.selected) {
            this.setState({ currentPage: 0 });
            this.props.getFlaggedEmails(this.props.selected, 0, numThreadsPerPage);
        }
    }

    handlePageChange = (_1: any, newPage: number) => {
        this.setState({ currentPage: newPage - 1 });
        this.props.getFlaggedEmails(this.props.selected, numThreadsPerPage * (newPage - 1), numThreadsPerPage);
    };

    handleReplyButton = (data: Communication) => () => {
        const { personIds, jobIds, accountInfo, account } = data;

        this.props.replyToEmail(
            [{ address: account, name: accountInfo.name.full }],
            this.props.emailContentBlacklistedDomains,
            data,
            true,
            true,
            {
                isClientComm: data.isClientComm,
                jobId: jobIds && jobIds.length === 1 ? jobIds[0] : null,
                personId: personIds && personIds.length === 1 ? personIds[0] : null
            }
        );
    };

    handleOpenMoreInfo = (data: Communication) => (event: React.MouseEvent<HTMLElement>) => {
        const moreInfoOpen = `${data.account}-${data.messageId}`;
        this.setState({ moreInfoOpen, moreInfoRef: event.currentTarget });
    };

    handleCloseMoreInfo = () => {
        this.setState({ moreInfoOpen: null, moreInfoRef: null });
    };

    handleCloseForwardSelector = () => {
        this.setState({ forwardSelectorOpen: null, forwardRef: null });
    };

    handleForwardButton = (data: Communication) => (event: React.MouseEvent<HTMLElement>) => {
        const forwardSelectorOpen = `${data.account}-${data.messageId}`;
        this.setState({ forwardSelectorOpen, forwardRef: event.currentTarget });
    };

    handleForwardToSelect = (data: Communication, email: string) => () => {
        this.props.forwardFlaggedEmail(data.account, data.messageId, email);
        this.handleCloseForwardSelector();
    };

    handleOpenIgnoreDialog = (data: Communication) => () => {
        this.setState({ ignoreDialogOpen: `${data.account}-${data.messageId}`, ignoreDialogData: data });
    };

    handleCloseIgnoreDialog = () => {
        this.setState({ ignoreDialogOpen: null, ignoreDialogData: null });
    };

    handleConfirmIgnoreSender = async (regexPattern: string) => {
        const { ignoreDialogData } = this.state;
        if (!ignoreDialogData) return;

        this.setState({ ignoreInProgress: true });
        this.handleCloseIgnoreDialog();

        const { account, messageId } = ignoreDialogData;

        try {
            const response = await requestAddSenderToIgnoredList(account, messageId, { regexPattern });
            if (response.success) {
                this.props.history.push('/');
                this.props.requestToasterOpen('Sender added to ignored list');
            } else {
                this.props.showModalAlert('Failed to add sender to ignored list', 'Error');
            }
        } catch (error) {
            this.props.showModalAlert('Error adding sender to ignored list', 'Error');
        }
        this.setState({ ignoreInProgress: false });
    };

    renderCommunicationComponent = (data: Communication) => {
        const hasError = data.matchData && data.matchData.error;

        const { threadId, personIds, jobIds, matchData, messageId } = data;
        const personIdsWithLinks =
            personIds && personIds.length > 0
                ? personIds.map((id) => (
                      <div key={id}>
                          <a target="_blank" href={`/person/${id}`}>
                              <PersonName id={id} />
                          </a>
                      </div>
                  ))
                : null;
        const jobIdsWithLinks =
            jobIds && jobIds.length > 0
                ? jobIds.map((id) => (
                      <a key={id} target="_blank" href={`/job/${id}/board`}>
                          <JobTitle id={id} />
                      </a>
                  ))
                : null;
        const matchReasons = [];
        if (matchData.multiMatch) {
            matchReasons.push(...matchData.multiMatch.map((r) => <div key={r}>{r}</div>));
        } else if (matchData.matchReason) {
            matchReasons.push(<div key={matchData.matchReason}>{matchData.matchReason}</div>);
        }
        if (matchData.manualOverrideJobIds) {
            matchReasons.push(<div key="manualOverrideJobIds">manualOverrideJobIds</div>);
        }
        if (matchData.manualOverridePersonIds) {
            matchReasons.push(<div key="manualOverridePersonIds">manualOverridePersonIds</div>);
        }

        const moreInfo = (
            <div className="flagged-emails-more-info">
                <div className="flagged-emails-more-info-title">Persons</div>
                <div className="flagged-emails-more-info-list">{personIdsWithLinks}</div>
                <div className="flagged-emails-more-info-title">Jobs</div>
                <div className="flagged-emails-more-info-list">{jobIdsWithLinks}</div>
                <div className="flagged-emails-more-info-title">Match Reasons</div>
                <div className="flagged-emails-more-info-list">{matchReasons}</div>
                <div className="flagged-emails-more-info-title">Debug Info</div>
                <div className="flagged-emails-more-info-list">
                    <div>ThreadId: {threadId}</div>
                    <div>MessageId: {messageId}</div>
                </div>
            </div>
        );

        const moreInfoIcon = (
            <div key="more-info">
                <IconButton
                    className="email-action-icon email-action more-info"
                    onClick={this.handleOpenMoreInfo(data)}
                >
                    <ActionInfo />
                </IconButton>
                <Popover
                    open={this.state.moreInfoOpen === `${data.account}-${data.messageId}`}
                    onClose={this.handleCloseMoreInfo}
                    anchorEl={this.state.moreInfoRef}
                    anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    {moreInfo}
                </Popover>
            </div>
        );

        const replyButton = (
            <CommunicationReplyButton key="reply-action" communication={data} onReply={this.handleReplyButton(data)} />
        );

        const spinnerSize = 20;
        const spinnerThickness = 2;
        const forwardToOpts = [
            <MenuItem
                key="clientcomms"
                value={null}
                onClick={this.handleForwardToSelect(data, clientCommsEmailAddress.address)}
            >
                {clientCommsEmailAddress.address}
            </MenuItem>
        ].concat(
            this.props.activeUsers
                .sort((u1, u2) => u1.name.full.localeCompare(u2.name.full))
                .map((u) => (
                    <MenuItem key={u.id} value={u.id} onClick={this.handleForwardToSelect(data, u.email)}>
                        {u.name.full}
                    </MenuItem>
                ))
        );
        const maxHeight = 400;
        const forwardButton = !hasRole(
            this.props.userPermissions,
            'email_match_setter'
        ) ? null : this.props.pendingRequests.has(`forward-flagged-email-${data.account}-${data.messageId}`) ? (
            <CircularProgress
                style={{ marginRight: 18 }}
                size={spinnerSize}
                thickness={spinnerThickness}
                key="forward"
            />
        ) : (
            <div className="email-action-icon email-action" key="forward">
                <IconButton className="email-action-icon" onClick={this.handleForwardButton(data)}>
                    <ContentForward style={{ color: 'black' }} />
                </IconButton>
                <Menu
                    anchorEl={this.state.forwardRef}
                    open={this.state.forwardSelectorOpen === `${data.account}-${data.messageId}`}
                    onClose={this.handleCloseForwardSelector}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    keepMounted={true}
                    MenuListProps={{ style: { maxHeight, overflowY: 'auto' } }}
                >
                    {forwardToOpts}
                </Menu>
            </div>
        );

        const ignoreButton = !hasRole(this.props.userPermissions, 'email_ignore_match_setter') ? null : (
            <Tooltip title="Add sender to ignored list">
                <span style={{ marginRight: 18 }}>
                    <IconButton
                        key="ignore-sender"
                        size="small"
                        className="email-action-icon email-action"
                        onClick={this.handleOpenIgnoreDialog(data)}
                        disabled={this.state.ignoreInProgress}
                    >
                        <Block fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );

        const actions = [ignoreButton, replyButton, forwardButton, moreInfoIcon];

        return (
            <div
                key={`${data.account}_${data.messageId}`}
                className={`flagged-emails-email ${hasError ? 'error' : ''}`}
            >
                <CommunicationComponent
                    personId={null}
                    data={data}
                    actions={actions}
                    initiallyExpanded={false}
                    emailContentBlacklistedDomains={this.props.emailContentBlacklistedDomains}
                />
            </div>
        );
    };

    render() {
        const { flaggedEmails, selected: account } = this.props;
        let content: JSX.Element;
        if (!flaggedEmails.get(account)) {
            content = <Spinner />;
        } else {
            const { threads, total } = this.props.flaggedEmails.get(this.props.selected);
            const groups = threads
                .valueSeq()
                .toArray()
                .map((thread) => {
                    if (thread.length === 0) return null;
                    const comms = thread.map(this.renderCommunicationComponent);
                    return (
                        <div className="emails-group" key={thread[0].threadId}>
                            {comms}
                        </div>
                    );
                });

            const pageCount = Math.ceil(total / numThreadsPerPage);
            const pagination =
                pageCount > 1 ? (
                    <div className="react-paginate">
                        <Pagination
                            count={pageCount}
                            page={this.state.currentPage + 1}
                            onChange={this.handlePageChange}
                            className="pagination"
                        />
                    </div>
                ) : null;

            content =
                total === 0 ? (
                    <div className="flagged-no-emails">No emails</div>
                ) : (
                    <div className="flagged-emails-list-container">
                        <div className="flagged-emails-list">{groups}</div>
                        {pagination}
                    </div>
                );
        }

        const ignoreDialog =
            this.state.ignoreDialogOpen && this.state.ignoreDialogData ? (
                <AddSenderToIgnoredListDialog
                    senderEmail={this.state.ignoreDialogData.headers.from.address}
                    onClose={this.handleCloseIgnoreDialog}
                    onConfirm={this.handleConfirmIgnoreSender}
                />
            ) : null;

        return (
            <div id="content" className="flex-fill">
                {content}
                {ignoreDialog}
            </div>
        );
    }
}

const mapStateToProps = (state: State) => ({
    emailContentBlacklistedDomains: state.appConstants.constants.emailContentBlacklistedDomains,
    flaggedEmails: state.flaggedEmails,
    pendingRequests: state.pendingRequests,
    sessionUser: state.session.user,
    userPermissions: state.session.userPermissions
});

const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    forwardFlaggedEmail,
    getFlaggedEmails,
    replyToEmail,
    requestCommunicationMatch,
    requestToasterOpen,
    showModalAlert,
    updateCommunicationMatchData
};

export const FlaggedEmails = withRouter(
    connect<ConnectedProps, ConnectedDispatch, OwnProps>(mapStateToProps, mapDispatchToProps)(FlaggedEmailsComponent)
);
